import React from 'react';

const SubMasterModal = ({ show, onClose, onSave, initial = {}, relatedOptions = [], relatedLabel = 'Related Items', showRelatedField = false }) => {
  const [name, setName] = React.useState(initial.name || '');
  const [active, setActive] = React.useState(initial.active ?? true);
  const [selectedRelated, setSelectedRelated] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    setName(initial.name || '');
    setActive(initial.active ?? true);
    // Initialize selected related items from initial data
    const initialRelated = initial.related_items || [];
    setSelectedRelated(initialRelated.map(item => item.id || item));
    setSearchTerm('');
  }, [initial]);

  if (!show) return null;

  // Filter options based on search term and exclude already selected
  const filteredOptions = relatedOptions.filter(
    (opt) => 
      !selectedRelated.includes(opt.id) && 
      opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddRelated = (id) => {
    setSelectedRelated((prev) => [...prev, id]);
    setSearchTerm('');
  };

  const handleRemoveRelated = (id) => {
    setSelectedRelated((prev) => prev.filter((rid) => rid !== id));
  };

  const getRelatedName = (id) => {
    const item = relatedOptions.find((opt) => opt.id === id);
    return item ? item.name : `ID: ${id}`;
  };

  return (
    <div className="modal-backdrop d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initial.id ? 'Edit' : 'Add'} {initial.title || 'Item'}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Name</label>
                <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              {/* Multi-select for related items - only shown for types that support relations */}
              {showRelatedField && (
                <div className="mb-3">
                  <label className="form-label">{relatedLabel}</label>
                  
                  {/* Selected tags */}
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {selectedRelated.map((id) => (
                      <span key={id} className="badge bg-primary d-flex align-items-center gap-1" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>
                        {getRelatedName(id)}
                        <button 
                          type="button" 
                          className="btn-close btn-close-white ms-1" 
                          style={{ fontSize: '0.6rem' }}
                          onClick={() => handleRemoveRelated(id)}
                          aria-label="Remove"
                        />
                      </span>
                    ))}
                  </div>

                  {/* Search and select dropdown */}
                  <div className="position-relative">
                    <input
                      className="form-control"
                      placeholder={`Search ${relatedLabel.toLowerCase()}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && filteredOptions.length > 0 && (
                      <div 
                        className="dropdown-menu show w-100" 
                        style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto',
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          zIndex: 1050
                        }}
                      >
                        {filteredOptions.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className="dropdown-item"
                            onClick={() => handleAddRelated(opt.id)}
                          >
                            {opt.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {searchTerm && filteredOptions.length === 0 && (
                      <div 
                        className="dropdown-menu show w-100" 
                        style={{ position: 'absolute', top: '100%', left: 0 }}
                      >
                        <span className="dropdown-item text-muted">No options found</span>
                      </div>
                    )}
                  </div>

                  {/* Quick select buttons for remaining options */}
                  {relatedOptions.length > 0 && filteredOptions.length > 0 && !searchTerm && (
                    <div className="mt-2">
                      <small className="text-muted">Click to add:</small>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {filteredOptions.slice(0, 5).map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleAddRelated(opt.id)}
                          >
                            + {opt.name}
                          </button>
                        ))}
                        {filteredOptions.length > 5 && (
                          <span className="text-muted align-self-center ms-1">
                            +{filteredOptions.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox" id="modalActive" checked={active} onChange={(e) => setActive(e.target.checked)} />
                <label className="form-check-label ms-2" htmlFor="modalActive">Active</label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => onSave({ ...initial, name: name.trim(), active, related_ids: selectedRelated })} 
                disabled={!name.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubMasterModal;
